/**
 * Service-worker registration.
 *
 * Production only. In development the dev server rebuilds assets on every
 * change, and a worker serving them cache-first is a reliable way to spend an
 * afternoon debugging a stale bundle.
 *
 * The worker itself (scripts/service-worker.template.js) caches only the app
 * shell and build output — never /api or /auth — so signing in as a different
 * user on a shared machine cannot surface the previous user's data.
 */

const SW_URL_PATH = '/service-worker.js';

const isSupported = () =>
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

export function register() {
  if (process.env.NODE_ENV !== 'production' || !isSupported()) return;

  // A worker can only control pages on its own origin. PUBLIC_URL is '/' here,
  // but if the build is ever moved behind a CDN on another host this guard
  // stops it from registering a worker that would never control anything.
  const publicUrl = new URL(process.env.PUBLIC_URL || '/', window.location.href);
  if (publicUrl.origin !== window.location.origin) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(`${publicUrl.pathname.replace(/\/$/, '')}${SW_URL_PATH}`)
      .catch((error) => {
        // Registration failing must never take the app down with it — the site
        // works exactly as before, just without offline support.
        console.error('Service worker registration failed:', error);
      });
  });
}

/**
 * Removes any worker previously registered by this app and drops its caches.
 *
 * This is the kill switch. If a released worker ever serves something stale or
 * wrong, swap the `register()` call in src/index.js for `unregister()` and
 * deploy: the next visit tears the old worker down. Keep that build up until
 * the bad worker is gone from the field, because a client that never loads the
 * replacement keeps the old one.
 */
export function unregister() {
  if (!isSupported()) return;

  navigator.serviceWorker.ready
    .then((registration) => registration.unregister())
    .then(() => (typeof caches !== 'undefined' ? caches.keys() : []))
    .then((keys) =>
      Promise.all(
        keys.filter((key) => key.startsWith('portal-')).map((key) => caches.delete(key))
      )
    )
    .catch((error) => {
      console.error('Service worker unregistration failed:', error);
    });
}
